"use client";
import { isAgentProfile } from "@/features/onboarding/agent-type";

import type { PrivacyConsentHandle } from "@/features/service-information/PrivacyConsent";
import { SupportWechatQrCode } from "@/features/service-information/SupportContact";
import { useLocalViewStep } from "./local-view-context";
import { GuidedSubmitButton } from "@/components/forms/GuidedSubmitButton";
import { SuppliedOnboardingDocuments, suppliedDocumentsForRole } from "./SuppliedOnboardingDocuments";
import { RegistrationConsentPreview, RegistrationInformationNotice } from "@/features/service-information/RegistrationConsent";
import Link from "next/link";
import { useRef, useState, type ReactNode } from "react";
import type { WorkspaceData } from "@/features/online-data/server/data";
import { ShieldCheck } from "@app-foundation/icons/lucide";
import { EnterpriseVerificationForm } from "./EnterpriseVerificationForm";
import { OnboardingFace } from "./OnboardingFace";
import { agentSteps, creatorSteps, merchantSteps, OnboardingSteps } from "./OnboardingSteps";
import { MerchantChoices } from "./RegistrationChoices";
import { OnboardingHeading } from "./OnboardingHeading";
import { acceptedSuppliedChoices, emptySuppliedChoices, suppliedDocumentSequence, type SuppliedConsentChoices } from "../supplied-documents";
import { OnboardingSigningDialog } from "./OnboardingSigningDialog";
import type { ConsentChoices } from "../consent";

type State = WorkspaceData<"onboarding">;
export type OnboardingPreview = {
  index: number;
  onSelect: (index: number) => void;
  verification: ReactNode;
  connection: ReactNode;
  failSigning?: boolean;
  documentSignatures?: { signed: Record<string, boolean>; choices: SuppliedConsentChoices };
  onDocumentSigned?: (id: string, choices?: SuppliedConsentChoices) => void;
};
// Retry only transient status reads; never replay consent, signing, or completion writes.
async function fetchOnboardingState() {
  let response = await fetch("/api/onboarding", { cache: "no-store" });
  if ([502, 503, 504].includes(response.status)) {
    await new Promise(resolve => setTimeout(resolve, 500));
    response = await fetch("/api/onboarding", { cache: "no-store" });
  }
  return response;
}

const button = "rg-primary";
export function OnboardingWizard({ initialState, nonce, previewOnly = false, faceReturn = false, preview, localTestNavigation = false }: { localTestNavigation?: boolean; initialState: State; nonce?: string; previewOnly?: boolean; faceReturn?: boolean; preview?: OnboardingPreview }) {
  const [state, setState] = useState(initialState);
  const agreementDone = state.agreement || Boolean(state.testAgreementSigned);
  const merchant = state.role === "partner";
  const agent = isAgentProfile(state);
  const documentRole = agent ? "agent" : merchant ? "merchant" : "creator";
  const [previewSignatures, setPreviewSignatures] = useState<Record<string, boolean>>({});
  const [previewChoices, setPreviewChoices] = useState<SuppliedConsentChoices>(emptySuppliedChoices);
  const documentReader = useRef<PrivacyConsentHandle>(null);
  const [formalSigningOpen, setFormalSigningOpen] = useState(false);
  const [formalRead, setFormalRead] = useState({ agreement: false, sensitive: false });
  const [formalChoices, setFormalChoices] = useState<ConsentChoices>(state.consentChoices ?? { portrait: "", identity: "", financial: "", agency: "", preview: "" });
  const signedDocuments = preview ? preview.documentSignatures?.signed ?? previewSignatures : state.documentSignatures?.signed ?? {};
  const requiredDocuments = suppliedDocumentsForRole(documentRole);
  const documentSequence = suppliedDocumentSequence(requiredDocuments, signedDocuments);
  const allDocumentsSigned = documentSequence.complete;
  const identityReady = merchant ? state.enterprise : Boolean(state.verificationExempt || (state.realname && state.face));
  const firstStep = state.completed || state.testCompleted ? "complete" : merchant && !agent && !state.merchantType ? "merchant" : !identityReady ? "verify" : "agreement";
  const [currentStep, setCurrentStep] = useState(preview ? preview.index < 3 ? "verify" : preview.index === 3 ? "agreement" : "complete" : firstStep);
  // Local navigation changes the displayed panel only, never authoritative state.
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const localIndex = selectedStep;
  const viewStep = useLocalViewStep();
  const step = localIndex === null ? currentStep : localIndex === 0 ? merchant && !agent ? "merchant" : "registration"
    : localIndex === 1 ? merchant ? "registration" : "verify" : localIndex === 2 ? "verify" : localIndex === 3 ? "agreement" : "complete";
  function setStep(value: string) { setSelectedStep(null); setCurrentStep(value); }
  const completionPreview = Boolean((preview && preview.index >= 4) || (localIndex !== null && localIndex >= 4 && !state.completed && !state.testCompleted));
  const [merchantChoice, setMerchantChoice] = useState<"director" | "brand" | null>(initialState.merchantType);
  const [signingContact, setSigningContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const steps = agent ? agentSteps : merchant ? merchantSteps : creatorSteps;
  const index = localIndex ?? preview?.index ?? (step === "merchant" || step === "agent-city" ? 0 : step === "verify" ? merchant ? 2 : state.realname ? 2 : 1 : step === "agreement" ? 3 : steps.length - 1);
  const previousIndex = step === "agreement" ? 2 : step === "verify" ? merchant ? 1 : 0 : step === "registration" && merchant && !agent ? 0 : null;
  function goBack() {
    if (busy || previousIndex === null) return;
    setError("");
    const select = preview?.onSelect ?? viewStep;
    if (select) select(previousIndex);
    else setSelectedStep(previousIndex);
  }
  function continuationReason() {
    if (step === "complete" || step === "registration") return "";
    if (previewOnly) return "入驻服务暂未开放，暂不能继续，请稍后再试。";
    if (busy) return "正在处理，请等待结果后再操作。";
    if (step === "merchant" && !merchantChoice) return "请先选择导演或电商身份。";
    if (step === "verify" && error && !merchant) return "认证状态尚未同步，请点击“重新同步认证状态”。";
    if (step === "verify" && !identityReady) return merchant
      ? "企业认证尚未通过，审核结果会自动同步。"
      : state.realname ? "人脸识别尚未通过，请先完成人脸核验；手机完成后会自动同步。" : "实名认证尚未通过，请先完成上方认证；手机完成后会自动同步。";
    if (step === "agreement" && agent && !identityReady) return "企业认证尚未通过，请先完成企业认证。";
    if (step === "agreement" && !agent && !allDocumentsSigned) return "请按顺序阅读并确认签署全部入驻文件。";
    return "";
  }
  const blockedReason = continuationReason();
  async function refresh(advanceAfterVerification = false) {
    if (preview) return;
    if (previewOnly || busy) return;
    setBusy(true); setError("");
    try { const response = await fetchOnboardingState(); const value = await response.json(); if (!response.ok) throw new Error(value.error); setState(value); if (value.completed || value.testCompleted) setStep("complete"); else if (!(value.role === "partner" ? value.enterprise : value.verificationExempt || (value.realname && value.face))) setStep("verify"); else if (advanceAfterVerification && (value.role === "partner" ? value.enterprise : value.realname && value.face)) setStep("agreement"); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "暂时无法核验，请重试"); }
    finally { setBusy(false); }
  }
  async function continueVerification() {
    if (previewOnly || busy || !identityReady) return;
    if (preview) { preview.onSelect(preview.index === 1 ? 2 : 3); return; }
    setBusy(true); setError("");
    try {
      const response = await fetch("/api/onboarding", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "verification" }) });
      const value = await response.json();
      if (!response.ok) throw new Error(value.error || "请先完成认证");
      if (!value.state?.identityReady) throw new Error("认证状态暂时无法核验，请刷新重试");
      setState(value.state); setStep("agreement");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "请先完成认证"); }
    finally { setBusy(false); }
  }
  async function submit(body: object) {
    if (preview) { preview.onSelect(5); return; }
    if (previewOnly || busy) return;
    setBusy(true); setError("");
    try {
      const response = await fetch("/api/onboarding", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      if (result.next) { setState(current => ({ ...current, ...(result.testCompleted ? { testCompleted: true } : { completed: true }) })); setStep("complete"); }
      else { const response = await fetchOnboardingState(); const value = await response.json(); if (!response.ok) throw new Error(value.error); setState(value); setStep("verify"); }
    } catch (reason) { setError(reason instanceof Error ? reason.message : "操作未完成，请重试"); }
    finally { setBusy(false); }
  }
  async function restartTest() {
    await submit({ action: "test-reset" });
  }
  async function signDocument(documentId: string, choices?: SuppliedConsentChoices, renderedDigest?: string) {
    if (busy || previewOnly) throw new Error("暂时无法签署，请稍后重试。");
    if (!documentSequence.isUnlocked(requiredDocuments.findIndex(document => document.id === documentId))) throw new Error("请先签署上一份协议。");
    if (documentId === "sensitive" && !acceptedSuppliedChoices(choices)) throw new Error("请按顺序逐项确认敏感信息处理后再签署。");
    if (preview) {
      if (preview.failSigning) throw new Error("签署未完成，请重试。");
      setPreviewSignatures(current => ({ ...current, [documentId]: true }));
      if (choices) setPreviewChoices(choices);
      preview.onDocumentSigned?.(documentId, choices);
      return;
    }
    setBusy(true); setError("");
    try {
      const response = await fetch("/api/onboarding", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        action: "document-sign", documentDigest: state.documentSignatures?.digest, documentId, confirmed: true, ...(renderedDigest !== undefined ? { renderedDigest } : {}), ...(documentId === "creator" && signingContact.trim() ? { contact: signingContact.trim() } : {}), ...(choices ? { choices } : {}),
      }) });
      const result = await response.json();
      if (!response.ok || !result.documentSignatures?.signed?.[documentId]) throw new Error(result.error || "签署结果尚未确认，请重试或刷新状态核对。");
      setState(current => ({ ...current, documentSignatures: result.documentSignatures }));
    } finally { setBusy(false); }
  }
  async function prepareDocument(id: string) {
    const response = await fetch("/api/onboarding", { method: "POST", cache: "no-store", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "document-prepare", documentId: id, ...(id === "creator" ? { contact: signingContact.trim() } : {}) }) });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "协议读取失败，请重试。");
    return result;
  }
  async function continueAgreement() {
    if ((!agent && !allDocumentsSigned) || busy || previewOnly) return;
    if (agent) { await submit({ action: "complete" }); return; }
    if (preview) { preview.onSelect(5); return; }
    setBusy(true); setError("");
    try {
      const response = await fetch("/api/onboarding", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "agreement" }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "签署尚未完成，请重试");
      if (!result.state?.documentSignatures?.complete || !result.state.identityReady || !(result.state.agreement || result.state.testAgreementSigned) || !result.state.consentReady) throw new Error("签署状态尚未确认，请刷新后核对");
      setState(result.state);
      const completionResponse = await fetch("/api/onboarding", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: result.state.verificationExempt && result.state.testAgreementSigned ? "test-complete" : "complete" }) });
      const completion = await completionResponse.json();
      if (!completionResponse.ok || !completion.next) throw new Error(completion.error || "入驻结果尚未确认，请刷新后核对");
      setState(current => ({ ...current, home: completion.next, ...(completion.testCompleted ? { testCompleted: true } : { completed: true }) }));
      setStep("complete");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "签署状态尚未确认，请刷新后核对"); }
    finally { setBusy(false); }
  }
  return <div className="rg-wrap">
    <OnboardingHeading merchant={merchant} agent={agent} merchantType={state.merchantType} />
    <OnboardingSteps labels={steps} current={index} onSelect={localTestNavigation ? position => { if (!busy && position >= 0 && position <= index && position < steps.length) { setSelectedStep(position); setError(""); } } : preview?.onSelect} completed={(merchant ? [Boolean(isAgentProfile(state) || state.merchantType), true, state.enterprise, state.agreement, state.linked, state.completed] : [true, identityReady, identityReady, agreementDone, state.linked, state.completed]).flatMap((done, position) => done ? [position] : [])} />
    <section className="rg-card">
      <div className="rg-step-content">
      {step === "registration" && <>
        {localTestNavigation ? <RegistrationConsentPreview /> : <p className="rg-status" role="status">注册账号已完成</p>}
        <div className="rg-actions"><button type="button" className={button} onClick={() => setSelectedStep(merchant ? 2 : 1)}>下一步</button></div>
        {localTestNavigation && <RegistrationInformationNotice />}
      </>}
      {state.verificationExempt && !preview && <button type="button" disabled={busy} className="rg-secondary mb-5" onClick={() => void restartTest()}>重新测试入驻</button>}
      {step === "merchant" && <>
        <MerchantChoices value={merchantChoice} onChange={selected => {
          setMerchantChoice(selected);
          if (preview) preview.onSelect(1);
          else void submit({ action: "merchant", merchantType: selected });
        }} disabled={busy || previewOnly} />
      </>}
      {step === "verify" && <>
        <h2 className="sr-only">{merchant ? "企业认证" : "活体认证"}</h2>
        {preview ? <div className="rg-verification">{preview.verification}</div> : merchant ? <div className="rg-verification"><EnterpriseVerificationForm unavailable={previewOnly} onVerified={() => void refresh(true)} /></div> : <div className="rg-verification">{previewOnly ? <p className="rg-notice">实名认证暂不可用。</p> : state.verificationExempt ? <p className="rg-notice">测试账号 · 已豁免网站认证</p> : <OnboardingFace key={localIndex} showForm={localIndex === 1} nonce={nonce} returning={faceReturn} passed={identityReady} onVerified={() => void refresh(true)} />}</div>}
        {(identityReady || (preview && !merchant)) && <div className="rg-actions"><GuidedSubmitButton type="button" className={button} disabled={busy || previewOnly} reason={blockedReason} onClick={() => void continueVerification()}>下一步</GuidedSubmitButton></div>}
      </>}
      {step === "agreement" && <>
        <h2 className="sr-only">{agent ? "联系我们" : "入驻协议"}</h2>
        {!merchant && !signedDocuments.creator && <label className="rg-field mb-4">协议联系方式<input value={signingContact} onChange={event => setSigningContact(event.target.value)} maxLength={128} autoComplete="off" /></label>}
        {agent ? <div className="mx-auto w-full max-w-72"><SupportWechatQrCode /></div> : <SuppliedOnboardingDocuments key={`${documentRole}:${state.documentSignatures?.digest ?? "preview"}`} role={documentRole} signed={signedDocuments} choices={preview ? preview.documentSignatures?.choices ?? previewChoices : state.documentSignatures?.choices} onSign={signDocument} prepareDocument={preview || state.verificationExempt ? undefined : prepareDocument} disabled={busy || previewOnly} hideSensitiveRow ref={documentReader} />}
        {state.verificationExempt && <p className="rg-notice">测试账号 · 已豁免网站认证</p>}
        <div className="rg-actions"><GuidedSubmitButton type="button" className={button} disabled={busy || previewOnly} reason={blockedReason} onBlocked={() => { if (!agent && !allDocumentsSigned) documentReader.current?.requestConsent(() => {}); }} onClick={() => {
          if (!preview && !merchant && (!agreementDone || !state.consentReady)) setFormalSigningOpen(true);
          else void continueAgreement();
        }}>{busy ? "正在保存…" : !preview && !merchant && allDocumentsSigned && (!agreementDone || !state.consentReady) ? agreementDone ? "确认信息处理" : "完成正式签署" : "下一步"}</GuidedSubmitButton></div>
        {formalSigningOpen && !preview && !merchant && <OnboardingSigningDialog initialDocument={0} signed={false} read={formalRead}
          onRead={(id, value) => setFormalRead(current => ({ ...current, [id]: value }))} choices={formalChoices} onChoice={setFormalChoices}
          consentReady={state.consentReady} testOnly={state.verificationExempt}
          onSigned={() => { void refresh(); }} onClose={() => setFormalSigningOpen(false)} />}
      </>}
      {step === "complete" && (state.completed || state.testCompleted || completionPreview) && <div className="rg-completion">
        <div className="rg-success-icon"><ShieldCheck size={42} strokeWidth={1.7} aria-hidden="true" /></div>
        <div className="rg-actions">
          <Link className={button} href={state.home}>完成入驻</Link>
        </div>
      </div>}
      {previousIndex !== null && <div className="rg-actions rg-back"><button type="button" className="rg-secondary" disabled={busy} onClick={goBack}>上一步</button></div>}
      {(error || state.message) && <p role="alert" className="rg-error">{error || state.message}</p>}
      {!preview && step !== "complete" && (step !== "verify" || (!merchant && error)) && <button type="button" disabled={busy || previewOnly} onClick={() => void refresh(step === "verify" && !merchant)} className="rg-refresh disabled:opacity-40">{busy ? "正在读取…" : step === "verify" ? "重新同步认证状态" : "刷新入驻状态"}</button>}
      </div>
    </section>
  </div>;
}
